/**
 * Manual Downloads Tab - Exports Page
 *
 * Build and download custom exports (CSV, Excel, JSON) with field selection and filters.
 */

import { useState } from 'react'
import {
  Card,
  Form,
  Radio,
  Transfer,
  Table,
  Button,
  Space,
  List,
  Typography,
  message,
} from 'antd'
import { DownloadOutlined, SaveOutlined, EyeOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { exportService } from '@/services/exportService'
import type { ExportParams, ExportTemplate, FilterRule } from '../types'

const { Text } = Typography

export function ManualDownloadsTab() {
  const queryClient = useQueryClient()

  // State
  const [format, setFormat] = useState<'csv' | 'excel' | 'json'>('csv')
  const [selectedFields, setSelectedFields] = useState<string[]>([])
  const [filters, setFilters] = useState<FilterRule[]>([])
  const [previewData, setPreviewData] = useState<any[]>([])

  // Fetch saved templates
  const { data: templatesResponse } = useQuery({
    queryKey: ['export-templates'],
    queryFn: () => exportService.getTemplates(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  const savedTemplates = templatesResponse?.data || []

  // Download export mutation
  const downloadExport = useMutation({
    mutationFn: (params: ExportParams) => exportService.download(params),
    onSuccess: (blob: Blob, variables: ExportParams) => {
      // Trigger browser download
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `export-${Date.now()}.${variables.format}`
      a.click()
      URL.revokeObjectURL(url)
      message.success('Export downloaded')
    },
    onError: () => {
      message.error('Failed to download export')
    },
  })

  // Save template mutation
  const saveTemplate = useMutation({
    mutationFn: (template: ExportTemplate) => exportService.saveTemplate(template),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['export-templates'] })
      message.success('Template saved')
      // TODO: Close modal when implemented
    },
    onError: () => {
      message.error('Failed to save template')
    },
  })

  // Mock available fields (in real impl, fetch from metadata service)
  const availableFields = [
    { key: 'uid', label: 'Product ID' },
    { key: 'name', label: 'Name' },
    { key: 'price', label: 'Price' },
    { key: 'cost', label: 'Cost' },
    { key: 'category', label: 'Category' },
    { key: 'sku', label: 'SKU' },
    { key: 'description', label: 'Description' },
  ]

  const handleExport = () => {
    if (selectedFields.length === 0) {
      message.warning('Please select at least one field to export')
      return
    }

    const params: ExportParams = {
      format,
      fields: selectedFields,
      filters,
      includeDeleted: false,
    }

    downloadExport.mutate(params)
  }

  const handlePreview = () => {
    // TODO: Fetch preview data from backend
    setPreviewData([
      { uid: '1', name: 'Product A', price: 10.0 },
      { uid: '2', name: 'Product B', price: 20.0 },
    ])
    message.success('Preview loaded')
  }

  const handleSaveTemplate = () => {
    // TODO: Open modal to collect template name/description
    const template: ExportTemplate = {
      name: `Template ${Date.now()}`,
      description: 'Manual export template',
      params: {
        format,
        fields: selectedFields,
        filters,
        includeDeleted: false,
      },
    }

    saveTemplate.mutate(template)
  }

  // Fix for Transfer component - convert Key[] to string[]
  const handleFieldChange = (targetKeys: React.Key[]) => {
    setSelectedFields(targetKeys as string[])
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Export Builder Form */}
      <Card title="Build Export">
        <Form layout="vertical">
          {/* Format Selection */}
          <Form.Item label="Export Format">
            <Radio.Group value={format} onChange={(e) => setFormat(e.target.value)}>
              <Radio.Button value="csv">CSV</Radio.Button>
              <Radio.Button value="excel">Excel</Radio.Button>
              <Radio.Button value="json">JSON</Radio.Button>
            </Radio.Group>
          </Form.Item>

          {/* Field Selection */}
          <Form.Item label="Select Fields to Export">
            <Transfer
              dataSource={availableFields}
              targetKeys={selectedFields}
              onChange={handleFieldChange}
              render={(item) => item.label}
              listStyle={{ width: 300, height: 300 }}
              titles={['Available Fields', 'Selected Fields']}
              showSearch
            />
          </Form.Item>

          {/* Filter Builder - Simplified for now */}
          <Form.Item label="Filters (Optional)">
            <Text type="secondary">Filter builder coming soon</Text>
          </Form.Item>

          {/* Preview Button */}
          <Form.Item>
            <Button onClick={handlePreview} icon={<EyeOutlined />}>
              Preview Export (First 10 Records)
            </Button>
          </Form.Item>

          {/* Preview Table */}
          {previewData.length > 0 && (
            <Form.Item>
              <Table
                dataSource={previewData}
                columns={selectedFields.map((field) => ({
                  title: availableFields.find((f) => f.key === field)?.label || field,
                  dataIndex: field,
                  key: field,
                }))}
                pagination={false}
                size="small"
                bordered
              />
            </Form.Item>
          )}

          {/* Action Buttons */}
          <Form.Item>
            <Space>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleExport}
                disabled={selectedFields.length === 0}
                loading={downloadExport.isPending}
              >
                Download Export
              </Button>
              <Button
                icon={<SaveOutlined />}
                onClick={handleSaveTemplate}
                disabled={selectedFields.length === 0}
              >
                Save as Template
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* Saved Export Templates */}
      <Card title="Saved Export Templates">
        {savedTemplates.length === 0 ? (
          <Text type="secondary">
            No saved templates yet. Create and save an export configuration above to reuse it
            later.
          </Text>
        ) : (
          <List
            dataSource={savedTemplates}
            renderItem={(template) => (
              <List.Item
                actions={[
                  <Button
                    key="load"
                    type="link"
                    onClick={() => {
                      setFormat(template.params.format)
                      setSelectedFields(template.params.fields)
                      setFilters(template.params.filters)
                      message.success(`Template "${template.name}" loaded`)
                    }}
                  >
                    Load
                  </Button>,
                  <Button
                    key="export"
                    type="link"
                    onClick={() => downloadExport.mutate(template.params)}
                  >
                    Export
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  title={template.name}
                  description={
                    template.description ||
                    `${template.params.fields.length} fields, ${template.params.format.toUpperCase()}`
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Card>
    </Space>
  )
}
